// Pure snapshot shape + builder for the offline library mirror. No prisma
// import on purpose: the route feeds it rows, tests feed it literals, and the
// /offline page imports the types into a client bundle.
import { basenameFromUploadPath } from "@/lib/backup";
import type { Condition } from "@/lib/condition";

export const OFFLINE_SNAPSHOT_VERSION = 1;

export type OfflineImageRef = { full: string; thumb: string };
export type OfflineTrack = {
  position: number;
  title: string;
  duration: string | null;
  lyrics: string | null;
};
export type OfflineLocation = { id: string; name: string; parentId: string | null; hidden: boolean };
export type OfflineBorrow = {
  entityType: "ITEM" | "BOOK";
  entityId: string;
  borrowerName: string;
  borrowedAt: string;
};

export type OfflineItem = {
  id: string;
  type: "CD" | "CASSETTE" | "VINYL";
  title: string;
  artist: string;
  year: number | null;
  originalYear: number | null;
  released: string | null;
  country: string | null;
  genres: string[];
  styles: string[];
  barcode: string | null;
  price: string | null;
  currency: string | null;
  notes: string | null;
  catalogNotes: string | null;
  mediaCondition: Condition | null;
  sleeveCondition: Condition | null;
  labels: { name: string; catno?: string | null }[] | null;
  credits: { name: string; role: string }[] | null;
  companies: { name: string; entityType: string }[] | null;
  locationId: string | null;
  cover: OfflineImageRef | null;
  photos: OfflineImageRef[];
  tracks: OfflineTrack[];
  appleMusicUrl: string | null;
  spotifyUrl: string | null;
  createdAt: string;
};

export type OfflineBook = {
  id: string;
  title: string;
  subtitle: string | null;
  author: string;
  format: "HARDCOVER" | "PAPERBACK" | "OTHER";
  isbn: string | null;
  publisher: string | null;
  publishedYear: number | null;
  language: string | null;
  pageCount: number | null;
  subjects: string[];
  description: string | null;
  price: string | null;
  currency: string | null;
  notes: string | null;
  locationId: string | null;
  cover: OfflineImageRef | null;
  photos: OfflineImageRef[];
  createdAt: string;
};

export type OfflineSnapshot = {
  version: number;
  generatedAt: string;
  username: string;
  booksEnabled: boolean;
  defaultLocationId: string | null;
  locations: OfflineLocation[];
  items: OfflineItem[];
  books: OfflineBook[];
  borrows: OfflineBorrow[];
};

function imageRef(uploadPath: string | null | undefined): OfflineImageRef | null {
  const base = basenameFromUploadPath(uploadPath);
  if (!base) return null;
  return { full: `/uploads/${base}`, thumb: `/uploads/thumb/${base}` };
}

function imageRefs(paths: string[]): OfflineImageRef[] {
  return paths.map(imageRef).filter((r): r is OfflineImageRef => r !== null);
}

/** Minimal structural input — matches the prisma rows the route selects. */
export type SnapshotInput = {
  username: string;
  booksEnabled: boolean;
  defaultLocationId: string | null;
  locations: { id: string; name: string; parentId: string | null; hidden: boolean }[];
  items: (Omit<
    OfflineItem,
    "price" | "cover" | "photos" | "tracks" | "createdAt" | "labels" | "credits" | "companies"
  > & {
    price: { toString(): string } | null;
    labels: unknown;
    credits: unknown;
    companies: unknown;
    coverImagePath: string | null;
    photos: string[];
    createdAt: Date;
    tracks: OfflineTrack[];
  })[];
  books: (Omit<OfflineBook, "price" | "cover" | "photos" | "createdAt"> & {
    price: { toString(): string } | null;
    coverImagePath: string | null;
    photos: string[];
    createdAt: Date;
  })[];
  borrows: {
    entityType: "ITEM" | "BOOK";
    entityId: string;
    borrowerName: string;
    borrowedAt: Date;
  }[];
};

export function buildOfflineSnapshot(input: SnapshotInput): OfflineSnapshot {
  return {
    version: OFFLINE_SNAPSHOT_VERSION,
    generatedAt: new Date().toISOString(),
    username: input.username,
    booksEnabled: input.booksEnabled,
    defaultLocationId: input.defaultLocationId,
    locations: input.locations.map((l) => ({
      id: l.id,
      name: l.name,
      parentId: l.parentId,
      hidden: l.hidden,
    })),
    items: input.items.map((item) => ({
      id: item.id,
      type: item.type,
      title: item.title,
      artist: item.artist,
      year: item.year,
      originalYear: item.originalYear,
      released: item.released,
      country: item.country,
      genres: item.genres,
      styles: item.styles,
      barcode: item.barcode,
      price: item.price != null ? item.price.toString() : null,
      currency: item.currency,
      notes: item.notes,
      catalogNotes: item.catalogNotes,
      mediaCondition: item.mediaCondition,
      sleeveCondition: item.sleeveCondition,
      labels: item.labels as OfflineItem["labels"],
      credits: item.credits as OfflineItem["credits"],
      companies: item.companies as OfflineItem["companies"],
      locationId: item.locationId,
      cover: imageRef(item.coverImagePath),
      photos: imageRefs(item.photos),
      tracks: item.tracks.map((t) => ({
        position: t.position,
        title: t.title,
        duration: t.duration,
        lyrics: t.lyrics,
      })),
      appleMusicUrl: item.appleMusicUrl,
      spotifyUrl: item.spotifyUrl,
      createdAt: item.createdAt.toISOString(),
    })),
    books: input.books.map((book) => ({
      id: book.id,
      title: book.title,
      subtitle: book.subtitle,
      author: book.author,
      format: book.format,
      isbn: book.isbn,
      publisher: book.publisher,
      publishedYear: book.publishedYear,
      language: book.language,
      pageCount: book.pageCount,
      subjects: book.subjects,
      description: book.description,
      price: book.price != null ? book.price.toString() : null,
      currency: book.currency,
      notes: book.notes,
      locationId: book.locationId,
      cover: imageRef(book.coverImagePath),
      photos: imageRefs(book.photos),
      createdAt: book.createdAt.toISOString(),
    })),
    borrows: input.borrows.map((b) => ({
      entityType: b.entityType,
      entityId: b.entityId,
      borrowerName: b.borrowerName,
      borrowedAt: b.borrowedAt.toISOString(),
    })),
  };
}

export function imageUrlsOf(s: OfflineSnapshot): { thumbs: string[]; fulls: string[] } {
  const thumbs = new Set<string>();
  const fulls = new Set<string>();
  const add = (ref: OfflineImageRef | null) => {
    if (!ref) return;
    thumbs.add(ref.thumb);
    fulls.add(ref.full);
  };
  for (const item of s.items) {
    add(item.cover);
    for (const p of item.photos) add(p);
  }
  for (const book of s.books) {
    add(book.cover);
    for (const p of book.photos) add(p);
  }
  return { thumbs: [...thumbs], fulls: [...fulls] };
}
